
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AccountTab } from './AccountTab';
import { SubscriptionsTab } from './SubscriptionsTab';
import { SettingsTab } from './SettingsTab';
import { ContactTab } from './ContactTab';
import { SecurityMonitorTab } from './SecurityMonitorTab';
import { StripeWebhookMonitor } from './StripeWebhookMonitor';
import { WebhookDiagnostics } from './WebhookDiagnostics';
import { GutterCleaningTab } from './GutterCleaningTab';
import { GutterAvailabilityTab } from './GutterAvailabilityTab';
import GutterBookingsTab from './GutterBookingsTab';

export function DashboardTabs() {
  const { isAdmin } = useAuth();
  const isMobile = useIsMobile();

  const tabsConfig = [
    { value: "account", label: isMobile ? "Account" : "Account", visible: true },
    { value: "subscriptions", label: isMobile ? "Subs" : "Subscriptions", visible: true },
    { value: "gutter-cleaning", label: isMobile ? "Gutters" : "Gutter Cleaning", visible: true },
    { value: "gutter-availability", label: isMobile ? "Avail" : "Gutter Availability", visible: isAdmin },
    { value: "gutter-bookings", label: isMobile ? "Booking" : "Gutter Bookings", visible: isAdmin },
    { value: "stripe-monitor", label: isMobile ? "Stripe" : "Stripe Monitor", visible: isAdmin },
    { value: "security", label: isMobile ? "Security" : "Security", visible: isAdmin },
    { value: "settings", label: isMobile ? "Settings" : "Settings", visible: true },
    { value: "contact", label: isMobile ? "Contact" : "Contact", visible: true },
  ].filter(tab => tab.visible);

  return (
    <Tabs defaultValue="account" className="space-y-4">
      <div className="overflow-x-auto">
        <ScrollArea className="w-full whitespace-nowrap">
          <TabsList className={`inline-flex h-10 items-center justify-start rounded-md bg-muted p-1 text-muted-foreground ${isMobile ? 'gap-1' : 'gap-2'}`}>
            {tabsConfig.map((tab) => (
              <TabsTrigger 
                key={tab.value}
                value={tab.value} 
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm ${isMobile ? 'min-w-[60px] text-xs px-2' : 'px-3'}`}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </ScrollArea>
      </div>

      <TabsContent value="account">
        <AccountTab />
      </TabsContent>

      <TabsContent value="subscriptions">
        <SubscriptionsTab />
      </TabsContent>

      <TabsContent value="gutter-cleaning">
        <GutterCleaningTab />
      </TabsContent>

      {isAdmin && (
        <>
          <TabsContent value="gutter-availability">
            <GutterAvailabilityTab />
          </TabsContent>

          <TabsContent value="gutter-bookings">
            <GutterBookingsTab />
          </TabsContent>
        </>
      )}

      {isAdmin && (
        <TabsContent value="stripe-monitor">
          <WebhookDiagnostics />
          <StripeWebhookMonitor />
        </TabsContent>
      )}

      {isAdmin && (
        <TabsContent value="security">
          <SecurityMonitorTab />
        </TabsContent>
      )}

      <TabsContent value="settings">
        <SettingsTab />
      </TabsContent>

      <TabsContent value="contact">
        <ContactTab />
      </TabsContent>
    </Tabs>
  );
}
